// ui/offlineModal.js
// Offline progress summary modal

import { gameState } from '../game/gameState.js';
import { formatOfflineTime } from '../systems/offlineProgress.js';
import { getItemName, getItemIcon } from '../data/items/index.js';
import { formatNumber } from '../utils/format.js';
import { skillRegistry, getSkillIcon } from '../core/skillRegistry.js';
import { SKILLS } from '../game/constants.js';
import logger from '../utils/logger.js';
import { showPyreVeilLoreQueue } from './pyreVeilLoreModal.js';

let modalElement = null;
let currentProgress = null;

/**
 * Get skill name from registry
 */
function getSkillName(skillId) {
    const skill = skillRegistry.get(skillId);
    return skill?.name || skillId;
}

/**
 * Render XP gains section
 */
function renderXpGains(xpGains, levelUps) {
    if (Object.keys(xpGains).length === 0) {
        return '';
    }

    const levelUpMap = {};
    for (const lu of levelUps) {
        levelUpMap[lu.skillId] = lu;
    }

    const items = Object.entries(xpGains).map(([skillId, xp]) => {
        const icon = getSkillIcon(skillId);
        const name = getSkillName(skillId);
        const levelUp = levelUpMap[skillId];

        let levelText = '';
        if (levelUp) {
            levelText = `<span class="offline-levelup">(Level ${levelUp.oldLevel} → ${levelUp.newLevel}!)</span>`;
        }

        return `
            <div class="offline-xp-item">
                <span class="offline-skill-icon">${icon}</span>
                <span class="offline-skill-name">${name}:</span>
                <span class="offline-xp-value">+${formatNumber(xp)} XP</span>
                ${levelText}
            </div>
        `;
    }).join('');

    return `
        <div class="offline-section">
            <h3 class="offline-section-title">XP Gained</h3>
            <div class="offline-xp-list">
                ${items}
            </div>
        </div>
    `;
}

/**
 * Render items collected section
 */
function renderItemGains(itemGains) {
    const items = Object.entries(itemGains);
    if (items.length === 0) {
        return '';
    }

    const itemsHTML = items.map(([itemId, quantity]) => {
        const name = getItemName(itemId);
        const icon = getItemIcon(itemId, quantity);

        return `
            <div class="offline-item">
                <span class="offline-item-icon">${icon}</span>
                <span class="offline-item-name">${name}</span>
                <span class="offline-item-quantity">x${formatNumber(quantity)}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="offline-section">
            <h3 class="offline-section-title">Items Collected</h3>
            <div class="offline-items-list">
                ${itemsHTML}
            </div>
        </div>
    `;
}

/**
 * Render materials consumed section (ores, herbs, gold, ashes, etc.)
 */
function renderMaterialsConsumed(progress) {
    const consumed = [];

    // Inventory materials
    if (progress.materialsConsumed) {
        for (const [itemId, qty] of Object.entries(progress.materialsConsumed)) {
            if (qty > 0) consumed.push({ itemId, qty });
        }
    }

    // BloomHold materials
    if (progress.bloomholdConsumed) {
        for (const [itemId, qty] of Object.entries(progress.bloomholdConsumed)) {
            if (qty > 0) {
                const existing = consumed.find(c => c.itemId === itemId);
                if (existing) existing.qty += qty;
                else consumed.push({ itemId, qty });
            }
        }
    }

    // Ashes
    if (progress.ashesConsumed > 0) {
        const existing = consumed.find(c => c.itemId === 'ashes');
        if (existing) existing.qty += progress.ashesConsumed;
        else consumed.push({ itemId: 'ashes', qty: progress.ashesConsumed });
    }

    // Gold
    const goldConsumed = progress.goldConsumed > 0;

    if (consumed.length === 0 && !goldConsumed) return '';

    const itemsHTML = consumed.map(({ itemId, qty }) => {
        return `<div class="offline-item consumed">
            <span class="offline-item-icon">${getItemIcon(itemId, qty)}</span>
            <span class="offline-item-name">${getItemName(itemId)}</span>
            <span class="offline-item-quantity">x${formatNumber(qty)}</span>
        </div>`;
    }).join('');

    const goldHTML = goldConsumed ? `<div class="offline-item consumed">
        <span class="offline-item-icon">${getItemIcon('coins')}</span>
        <span class="offline-item-name">Gold</span>
        <span class="offline-item-quantity">x${formatNumber(progress.goldConsumed)}</span>
    </div>` : '';

    return `
        <div class="offline-section offline-consumed">
            <h3 class="offline-section-title">Materials Used</h3>
            <div class="offline-items-list">${itemsHTML}${goldHTML}</div>
        </div>
    `;
}

/**
 * Render overflow section
 */
function renderOverflow(overflowItems, offlineSeconds) {
    const items = Object.entries(overflowItems);
    if (items.length === 0) {
        return '';
    }

    const totalOverflow = items.reduce((sum, [_, qty]) => sum + qty, 0);

    const itemsHTML = items.map(([itemId, quantity]) => {
        const name = getItemName(itemId);
        return `${name} x${formatNumber(quantity)}`;
    }).join(', ');

    // Show warning about inventory being full and progress being limited
    const timeDisplay = formatOfflineTime(offlineSeconds);
    const limitedMessage = offlineSeconds >= 28800 // 8 hours in seconds
        ? `Your progress may have been limited during the ${timeDisplay} you were away.`
        : '';

    return `
        <div class="offline-section offline-overflow">
            <h3 class="offline-section-title">⚠️ Inventory Full</h3>
            <p class="offline-overflow-warning">
                Your inventory filled up during offline progress!
                ${limitedMessage}
            </p>
            <p class="offline-overflow-text">
                ${formatNumber(totalOverflow)} items could not fit: ${itemsHTML}
            </p>
            <div class="offline-overflow-actions">
                <button class="offline-btn" onclick="window.offlineModal.sendToBank()">
                    Send to Bank
                </button>
                <button class="offline-btn secondary" onclick="window.offlineModal.discardOverflow()">
                    Discard
                </button>
            </div>
        </div>
    `;
}

/**
 * Render combat progress section
 */
function renderCombatProgress(combatProgress) {
    if (!combatProgress || combatProgress.kills === 0) {
        return '';
    }

    // Get attack style icon
    const styleIcons = { melee: '⚔️', ranged: '🏹', magic: '✨' };
    const styleIcon = styleIcons[combatProgress.attackStyle] || '⚔️';

    // Format stats
    const statsHTML = `
        <div class="offline-combat-stats">
            <div class="combat-stat">
                <span class="combat-stat-icon">${styleIcon}</span>
                <span class="combat-stat-label">Kills:</span>
                <span class="combat-stat-value">${formatNumber(combatProgress.kills)}</span>
            </div>
            ${combatProgress.deaths > 0 ? `
                <div class="combat-stat">
                    <span class="combat-stat-icon">💀</span>
                    <span class="combat-stat-label">Deaths:</span>
                    <span class="combat-stat-value">${combatProgress.deaths}</span>
                </div>
            ` : ''}
            ${combatProgress.foodConsumed > 0 ? `
                <div class="combat-stat">
                    <span class="combat-stat-icon">🍖</span>
                    <span class="combat-stat-label">Food Used:</span>
                    <span class="combat-stat-value">${combatProgress.foodConsumed}</span>
                </div>
            ` : ''}
            ${combatProgress.arrowsConsumed > 0 ? `
                <div class="combat-stat">
                    <span class="combat-stat-icon">🏹</span>
                    <span class="combat-stat-label">Arrows Used:</span>
                    <span class="combat-stat-value">${formatNumber(combatProgress.arrowsConsumed)}</span>
                </div>
            ` : ''}
            ${combatProgress.glyphsConsumed > 0 ? `
                <div class="combat-stat">
                    <span class="combat-stat-icon">✨</span>
                    <span class="combat-stat-label">Glyphs Used:</span>
                    <span class="combat-stat-value">${formatNumber(combatProgress.glyphsConsumed)}</span>
                </div>
            ` : ''}
            <div class="combat-stat">
                <span class="combat-stat-icon">❤️</span>
                <span class="combat-stat-label">Final HP:</span>
                <span class="combat-stat-value">${combatProgress.finalHp}</span>
            </div>
        </div>
    `;

    return `
        <div class="offline-section offline-combat">
            <h3 class="offline-section-title">${styleIcon} Combat in ${combatProgress.zoneName}</h3>
            ${statsHTML}
        </div>
    `;
}

/**
 * Render guild (pilfering) loot section
 */
function renderGuildLoot(guildProgress, hasAutoLoot) {
    if (!guildProgress || guildProgress.totalItemsGathered === 0) {
        return '';
    }

    const items = Object.entries(guildProgress.itemsGathered);
    const itemsHTML = items.map(([itemId, quantity]) => {
        const name = getItemName(itemId);
        const icon = getItemIcon(itemId, quantity);
        return `
            <div class="offline-item">
                <span class="offline-item-icon">${icon}</span>
                <span class="offline-item-name">${name}</span>
                <span class="offline-item-quantity">x${formatNumber(quantity)}</span>
            </div>
        `;
    }).join('');

    const destination = hasAutoLoot ? 'to Bank' : 'to Guild Chest';
    const headerText = `Pilfering Guild ${destination}`;
    const chestWarning = guildProgress.chestWasFull && !hasAutoLoot
        ? '<p class="offline-chest-warning">⚠️ Guild chest filled up - some items may have been lost.</p>'
        : '';
    const pilferingIcon = getSkillIcon(SKILLS.PILFERING);

    return `
        <div class="offline-section offline-guild">
            <h3 class="offline-section-title">${pilferingIcon} ${headerText}</h3>
            <p class="offline-guild-count">${formatNumber(guildProgress.totalItemsGathered)} items gathered</p>
            ${chestWarning}
            <div class="offline-items-list">
                ${itemsHTML}
            </div>
        </div>
    `;
}

/**
 * Render pet contract loot section
 */
function renderPetLoot(petProgress, hasAutoLoot) {
    if (!petProgress || petProgress.completedContracts.length === 0) {
        return '';
    }

    const items = Object.entries(petProgress.petItemGains || {});
    let itemsHTML = '';
    if (items.length > 0) {
        itemsHTML = items.map(([itemId, quantity]) => {
            const name = getItemName(itemId);
            const icon = getItemIcon(itemId, quantity);
            return `
                <div class="offline-item">
                    <span class="offline-item-icon">${icon}</span>
                    <span class="offline-item-name">${name}</span>
                    <span class="offline-item-quantity">x${formatNumber(quantity)}</span>
                </div>
            `;
        }).join('');
    }

    const contractCount = petProgress.completedContracts.length;
    const destination = hasAutoLoot ? 'to Bank' : 'to Pet Loot Chest';
    const headerText = `Pet Contracts ${destination}`;
    const husbandryIcon = getSkillIcon(SKILLS.HUSBANDRY);

    return `
        <div class="offline-section offline-pets">
            <h3 class="offline-section-title">${husbandryIcon} ${headerText}</h3>
            <p class="offline-pets-count">${contractCount} contract${contractCount !== 1 ? 's' : ''} completed</p>
            ${items.length > 0 ? `
                <div class="offline-items-list">
                    ${itemsHTML}
                </div>
            ` : ''}
        </div>
    `;
}

/**
 * Render farm progress section
 */
function renderFarmProgress(farmProgress) {
    if (!farmProgress || farmProgress.completedPlots.length === 0) {
        return '';
    }

    const destinationMap = { bank: 'Bank', bloomhold: 'BloomHold', inventory: 'Inventory' };

    // Auto-harvest: show items received. No auto-harvest: just note crops are ready.
    if (farmProgress.autoHarvestEnabled) {
        const destination = destinationMap[farmProgress.autoHarvestDestination] || 'Inventory';

        let itemsHTML = '';
        if (farmProgress.farmItemGains) {
            const itemsList = Object.entries(farmProgress.farmItemGains).map(([itemId, qty]) => {
                const name = getItemName(itemId);
                const icon = getItemIcon(itemId, qty);
                return `<span class="farm-item">${icon} ${name} x${formatNumber(qty)}</span>`;
            }).join(', ');

            if (itemsList) {
                itemsHTML = `<p class="offline-farm-items">Collected: ${itemsList}</p>`;
            }
        }

        return `
            <div class="offline-section offline-farm">
                <h3 class="offline-section-title">🌾 Auto-Harvested to ${destination}</h3>
                ${itemsHTML}
            </div>
        `;
    }

    // No auto-harvest - just tell them crops are waiting
    const plotCount = farmProgress.completedPlots.length;
    return `
        <div class="offline-section offline-farm">
            <h3 class="offline-section-title">🌾 ${plotCount} Crop${plotCount !== 1 ? 's' : ''} Ready for Harvest</h3>
        </div>
    `;
}

/**
 * Create the offline modal HTML
 */
function createModalHTML(progress) {
    const timeDisplay = formatOfflineTime(progress.offlineSeconds);

    let actionContentHTML = '';
    let farmContentHTML = renderFarmProgress(progress.farmProgress);
    let combatContentHTML = renderCombatProgress(progress.combatProgress);

    // Check if auto-loot is enabled (Architect's Sanctum)
    const hasAutoLoot = gameState.hasAutoLootChests();
    let guildContentHTML = renderGuildLoot(progress.guildProgress, hasAutoLoot);
    let petContentHTML = renderPetLoot(progress.petProgress, hasAutoLoot);

    // Check if we have passive XP gains (guild, pets, etc) even without main action
    const hasPassiveXp = Object.keys(progress.xpGains || {}).length > 0;
    const hasPassiveItems = Object.keys(progress.itemGains || {}).length > 0;

    if (progress.hadAction && progress.actionsCompleted > 0) {
        // For combat, show the combat section instead of generic action content
        if (progress.actionType === 'combat') {
            actionContentHTML = `
                ${combatContentHTML}
                ${renderXpGains(progress.xpGains, progress.levelUps)}
                ${renderItemGains(progress.itemGains)}
                ${renderOverflow(progress.overflowItems, progress.offlineSeconds)}
            `;
            combatContentHTML = ''; // Already included above
        } else {
            actionContentHTML = `
                <p class="offline-action">Continued: <strong>${progress.actionName || 'Unknown Action'}</strong></p>
                <p class="offline-actions-count">${formatNumber(progress.actionsCompleted)} actions completed</p>
                ${renderXpGains(progress.xpGains, progress.levelUps)}
                ${renderItemGains(progress.itemGains)}
                ${renderMaterialsConsumed(progress)}
                ${renderOverflow(progress.overflowItems, progress.offlineSeconds)}
            `;
        }
    } else if (progress.hadAction) {
        actionContentHTML = `
            <p class="offline-action">You were working on: <strong>${progress.actionName || 'Unknown Action'}</strong></p>
            <p class="offline-no-progress">Not enough time passed to complete any actions.</p>
            ${renderXpGains(progress.xpGains, progress.levelUps)}
            ${renderItemGains(progress.itemGains)}
        `;
    } else if (hasPassiveXp || hasPassiveItems || farmContentHTML || combatContentHTML || guildContentHTML || petContentHTML) {
        // No main action, but have passive progress (guild, pets, farm, combat)
        actionContentHTML = `
            ${renderXpGains(progress.xpGains, progress.levelUps)}
            ${renderItemGains(progress.itemGains)}
        `;
    } else {
        // Nothing at all
        actionContentHTML = `
            <p class="offline-no-action">No action was running when you left.</p>
        `;
    }

    // Combine guild and pet sections side by side if both exist
    let passiveProgressHTML = '';
    if (guildContentHTML || petContentHTML) {
        if (guildContentHTML && petContentHTML) {
            // Both exist - put them side by side
            passiveProgressHTML = `
                <div class="offline-passive-row">
                    ${guildContentHTML}
                    ${petContentHTML}
                </div>
            `;
        } else {
            // Only one exists - render normally
            passiveProgressHTML = guildContentHTML + petContentHTML;
        }
    }

    return `
        <div class="modal-overlay" id="offline-modal-overlay">
            <div class="modal-content offline-modal">
                <div class="modal-header">
                    <h2>Welcome Back!</h2>
                    <button class="modal-close" onclick="window.offlineModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    <p class="offline-time">You were away for <strong>${timeDisplay}</strong></p>
                    ${actionContentHTML}
                    ${farmContentHTML}
                    ${passiveProgressHTML}
                    <div class="offline-claim">
                        <button class="offline-btn primary" onclick="window.offlineModal.claim()">
                            Claim Rewards
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Show the offline progress modal
 * @param {object} progress - Progress result from calculateOfflineProgress
 */
export function showOfflineModal(progress) {
    if (!progress) return;

    closeOfflineModal();
    currentProgress = progress;

    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = createModalHTML(progress);
    modalElement = modalContainer.firstElementChild;
    document.body.appendChild(modalElement);

    // Don't close on overlay click - user must click claim
    document.addEventListener('keydown', handleEscapeKey);

    logger.debug('[OFFLINE] Modal shown');
}

/**
 * Close the offline modal
 */
export function closeOfflineModal() {
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
        currentProgress = null;
        document.removeEventListener('keydown', handleEscapeKey);
        logger.debug('[OFFLINE] Modal closed');
    }
}

/**
 * Handle Escape key
 */
function handleEscapeKey(e) {
    if (e.key === 'Escape') {
        closeOfflineModal();
    }
}

/**
 * Claim rewards and close modal
 */
export function claimRewards() {
    closeOfflineModal();

    // Show any queued Pyre Veil lore from offline level-ups
    const queuedLore = gameState.popLoreQueue();
    if (queuedLore.length > 0) {
        showPyreVeilLoreQueue(queuedLore);
    }
}

/**
 * Send overflow items to bank
 */
export function sendOverflowToBank() {
    if (currentProgress && currentProgress.overflowItems) {
        for (const [itemId, quantity] of Object.entries(currentProgress.overflowItems)) {
            gameState.addBankItem(itemId, quantity);
        }
        currentProgress.overflowItems = {};
        logger.debug('[OFFLINE] Overflow items sent to bank');
    }

    // Update the modal to remove overflow section
    if (modalElement) {
        const overflowSection = modalElement.querySelector('.offline-overflow');
        if (overflowSection) {
            overflowSection.remove();
        }
    }
}

/**
 * Discard overflow items
 */
export function discardOverflow() {
    if (currentProgress) {
        currentProgress.overflowItems = {};
    }

    // Update the modal to remove overflow section
    if (modalElement) {
        const overflowSection = modalElement.querySelector('.offline-overflow');
        if (overflowSection) {
            overflowSection.remove();
        }
    }
}

// Expose to window for onclick handlers
window.offlineModal = {
    show: showOfflineModal,
    close: closeOfflineModal,
    claim: claimRewards,
    sendToBank: sendOverflowToBank,
    discardOverflow
};

export default {
    showOfflineModal,
    closeOfflineModal,
    claimRewards,
    sendOverflowToBank,
    discardOverflow
};
